import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  CreditCard, Calendar, Users, MapPin, Clock, Check, AlertCircle, 
  ChevronRight, Download, DollarSign, ArrowRight, CircleDollarSign
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LiveInvoicePage() {
  const { quoteId } = useParams();
  const [searchParams] = useSearchParams();
  const [plan, setPlan] = useState(null);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);

  const fetchPlan = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/payment-plans/by-quote/${quoteId}`);
      setPlan(res.data.plan);
      setQuote(res.data.quote);
    } catch {
      // Maybe no plan — try just the quote
      try {
        const qRes = await axios.get(`${API}/quotes/${quoteId}`);
        setQuote(qRes.data);
      } catch {
        toast.error('Invoice not found');
      }
    } finally {
      setLoading(false);
    }
  }, [quoteId]);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  // Verify payment if returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const depositPaid = searchParams.get('deposit_paid');
    const paymentSuccess = searchParams.get('payment_success');
    const paidInFull = searchParams.get('paid_in_full');

    if (sessionId && plan) {
      const verify = async () => {
        try {
          await axios.post(`${API}/payment-plans/${plan.plan_id}/verify-payment/${sessionId}`, {});
          toast.success(
            depositPaid ? 'Deposit paid successfully!' : 
            paidInFull ? 'Paid in full! Your event is fully secured.' :
            paymentSuccess ? 'Payment recorded!' : 'Payment verified!'
          );
          fetchPlan();
        } catch (err) {
          console.error('Verify error:', err);
        }
      };
      verify();
    }
  }, [searchParams, plan?.plan_id, fetchPlan]);

  const handlePayDeposit = async () => {
    setPayingId('deposit');
    try {
      const res = await axios.post(`${API}/payment-plans/${plan.plan_id}/pay-deposit`, {
        origin_url: window.location.origin
      });
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error('Failed to start payment');
    } finally {
      setPayingId(null);
    }
  };

  const handlePayInstallment = async (installmentId) => {
    setPayingId(installmentId);
    try {
      const res = await axios.post(`${API}/payment-plans/${plan.plan_id}/pay-installment/${installmentId}`, {
        origin_url: window.location.origin
      });
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error('Failed to start payment');
    } finally {
      setPayingId(null);
    }
  };

  const handlePayRemaining = async () => {
    setPayingId('remaining');
    try {
      const res = await axios.post(`${API}/payment-plans/${plan.plan_id}/pay-remaining`, {
        origin_url: window.location.origin
      });
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error('Failed to start payment');
    } finally {
      setPayingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const totalPaid = plan ? (
    (plan.deposit_status === 'paid' ? plan.deposit_amount : 0) + (plan.total_paid || 0)
  ) : 0;
  const remaining = plan ? Math.max(0, plan.total - totalPaid) : 0;
  const progressPercent = plan ? Math.min(100, (totalPaid / plan.total) * 100) : 0;
  const allPaid = plan?.status === 'completed' || remaining <= 0.01;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pt-20">
        <div className="animate-pulse text-muted-foreground">Loading invoice...</div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pt-20">
        <Card className="max-w-md"><CardContent className="p-8 text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Invoice Not Found</h2>
          <p className="text-muted-foreground">This invoice link may be expired or invalid.</p>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="max-w-3xl mx-auto px-4 space-y-6">
        
        {/* Header */}
        <div className="text-center" data-testid="invoice-header">
          <h1 className="text-3xl font-bold tracking-tight">Live Invoice</h1>
          <p className="text-muted-foreground mt-1">
            {plan ? `Payment Plan for ${plan.customer_name}` : `Invoice for ${quote.customer_name}`}
          </p>
          <Badge className="mt-2" variant={allPaid ? 'default' : 'secondary'} data-testid="invoice-status-badge">
            {allPaid ? 'Paid in Full' : plan?.deposit_status === 'paid' ? 'Deposit Paid' : 'Pending'}
          </Badge>
        </div>

        {/* Progress Bar */}
        {plan && (
          <Card data-testid="payment-progress">
            <CardContent className="p-6">
              <div className="flex justify-between items-end mb-3">
                <div>
                  <p className="text-sm text-muted-foreground">Total Paid</p>
                  <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">${totalPaid.toFixed(2)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Remaining</p>
                  <p className="text-2xl font-bold">${remaining.toFixed(2)}</p>
                </div>
              </div>
              <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-700"
                  style={{ width: `${progressPercent}%` }}
                  data-testid="progress-bar"
                />
              </div>
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>{progressPercent.toFixed(0)}% paid</span>
                <span>Total: ${plan.total.toFixed(2)}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Event Details */}
        <Card data-testid="event-details-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Event Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Date</p>
                  <p className="font-medium">{formatDate(quote.event_date)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Time</p>
                  <p className="font-medium">{quote.event_time || 'TBD'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Guests</p>
                  <p className="font-medium">{quote.guest_count}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Location</p>
                  <p className="font-medium">ZIP {quote.venue_zip_code}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Services */}
        <Card data-testid="services-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Services</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(quote.items || []).map((item, idx) => (
              <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                <div>
                  <p className="font-medium">{item.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {item.category} 
                    {item.is_dropoff && <Badge variant="outline" className="ml-2 text-xs">Drop-off</Badge>}
                    {item.guest_count && ` · ${item.guest_count} guests`}
                    {item.hours && ` · ${item.hours} hrs`}
                  </p>
                </div>
                <span className="font-semibold">${item.total_price?.toFixed(2)}</span>
              </div>
            ))}
            
            {/* Totals */}
            <div className="pt-3 space-y-1 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span>${quote.subtotal?.toFixed(2)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>Service Fee (15%)</span><span>${quote.admin_fee?.toFixed(2)}</span></div>
              {quote.tax > 0 && (
                <div className="flex justify-between text-muted-foreground"><span>Tax ({quote.tax_rate}%)</span><span>${quote.tax?.toFixed(2)}</span></div>
              )}
              <div className="flex justify-between font-bold text-lg pt-2 border-t"><span>Total</span><span className="text-indigo-600 dark:text-indigo-400">${quote.total?.toFixed(2)}</span></div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Schedule */}
        {plan && (
          <Card data-testid="payment-schedule-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <CircleDollarSign className="h-5 w-5" /> Payment Schedule
              </CardTitle>
              <CardDescription>
                {plan.auto_charge ? 'Auto-pay enabled — your card will be charged on each date' : 'Manual payments — pay on your schedule or early'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              
              {/* Deposit */}
              <div className={`flex items-center justify-between p-4 rounded-lg border-2 ${plan.deposit_status === 'paid' ? 'border-green-500/30 bg-green-50 dark:bg-green-950/20' : 'border-indigo-500/30 bg-indigo-50 dark:bg-indigo-950/20'}`} data-testid="deposit-row">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${plan.deposit_status === 'paid' ? 'bg-green-500 text-white' : 'bg-indigo-500 text-white'}`}>
                    {plan.deposit_status === 'paid' ? <Check className="h-4 w-4" /> : <DollarSign className="h-4 w-4" />}
                  </div>
                  <div>
                    <p className="font-semibold">50% Deposit</p>
                    <p className="text-xs text-muted-foreground">
                      {plan.deposit_status === 'paid' 
                        ? `Paid ${formatDate(plan.deposit_paid_at)}`
                        : 'Due at booking'
                      }
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-bold text-lg">${plan.deposit_amount.toFixed(2)}</span>
                  {plan.deposit_status === 'paid' ? (
                    <Badge className="bg-green-500 text-white border-0">Paid</Badge>
                  ) : (
                    <Button size="sm" onClick={handlePayDeposit} disabled={payingId === 'deposit'} data-testid="pay-deposit-btn">
                      {payingId === 'deposit' ? 'Processing...' : 'Pay Now'}
                    </Button>
                  )}
                </div>
              </div>

              {/* Installments */}
              {plan.installments.map((inst, idx) => {
                const isPaid = inst.status === 'paid';
                const isOverdue = !isPaid && new Date(inst.due_date) < new Date();
                return (
                  <div 
                    key={inst.installment_id} 
                    className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                      isPaid ? 'border-green-500/30 bg-green-50 dark:bg-green-950/20' 
                      : isOverdue ? 'border-red-500/30 bg-red-50 dark:bg-red-950/20'
                      : 'border-border'
                    }`}
                    data-testid={`installment-schedule-${idx}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        isPaid ? 'bg-green-500 text-white' 
                        : isOverdue ? 'bg-red-500 text-white'
                        : 'bg-muted text-muted-foreground'
                      }`}>
                        {isPaid ? <Check className="h-4 w-4" /> : idx + 1}
                      </div>
                      <div>
                        <p className="font-medium">Payment {idx + 1}</p>
                        <p className="text-xs text-muted-foreground">
                          {isPaid ? `Paid ${formatDate(inst.paid_at)}` : `Due ${formatDate(inst.due_date)}`}
                          {isOverdue && !isPaid && <span className="text-red-500 ml-1 font-semibold">Overdue</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold">${inst.amount.toFixed(2)}</span>
                      {isPaid ? (
                        <Badge className="bg-green-500 text-white border-0">Paid</Badge>
                      ) : plan.deposit_status === 'paid' ? (
                        <Button 
                          size="sm" 
                          variant={isOverdue ? 'destructive' : 'outline'}
                          onClick={() => handlePayInstallment(inst.installment_id)} 
                          disabled={payingId === inst.installment_id}
                          data-testid={`pay-installment-${idx}`}
                        >
                          {payingId === inst.installment_id ? '...' : 'Pay Now'}
                        </Button>
                      ) : (
                        <Badge variant="secondary">Pending</Badge>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Pay Remaining Balance */}
              {!allPaid && plan.deposit_status === 'paid' && remaining > 0.01 && (
                <div className="pt-3 border-t">
                  <Button 
                    className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700" 
                    size="lg"
                    onClick={handlePayRemaining}
                    disabled={payingId === 'remaining'}
                    data-testid="pay-remaining-btn"
                  >
                    <CreditCard className="mr-2 h-5 w-5" />
                    {payingId === 'remaining' ? 'Processing...' : `Pay Remaining Balance — $${remaining.toFixed(2)}`}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center mt-2">
                    Settle all outstanding installments at once
                  </p>
                </div>
              )}
              
              {allPaid && (
                <div className="text-center py-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-500/30" data-testid="fully-paid-banner">
                  <Check className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <p className="font-bold text-green-700 dark:text-green-400 text-lg">Fully Paid!</p>
                  <p className="text-sm text-muted-foreground">Your event is secured. We can't wait to make it unforgettable!</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* No plan — full payment invoice */}
        {!plan && quote.status === 'paid' && (
          <Card data-testid="full-payment-card">
            <CardContent className="p-6 text-center">
              <Check className="h-12 w-12 text-green-500 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-green-700 dark:text-green-400">Paid in Full</h2>
              <p className="text-muted-foreground mt-1">Thank you! Your event is fully secured.</p>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-muted-foreground space-y-1 pt-4">
          <p>Questions about your invoice? Contact us at support@eventism.com</p>
          <p>Invoice ID: {quoteId}</p>
          <p>Full refund if cancelled 30+ days before event. 50% refund 15-29 days. Non-refundable within 14 days.</p>
        </div>
      </div>
    </div>
  );
}
